import { Textarea } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { useUpdateAnonymizerConfig, useAnonymizerConfig } from '@/entities/anonymizer-config';
import type { PassportConfig } from '@/entities/anonymizer-config';
import { ConfigFormLayout } from './ConfigFormLayout';

type FormValues = {
  fullPassportRegex: string;
  contextMarkers: string;
  lookbackWindow: string;
  partialPassportRegex: string;
  partialContextMarkers: string;
  partialLookbackWindow: string;
  validationRegex: string;
};

const getDefaultValues = (config: PassportConfig | undefined): FormValues => ({
  fullPassportRegex: config?.fullPassportRegex ?? '',
  contextMarkers: config?.contextMarkers?.join('\n') ?? '',
  lookbackWindow: String(config?.lookbackWindow ?? ''),
  partialPassportRegex: config?.partialPassportRegex ?? '',
  partialContextMarkers: config?.partialContextMarkers?.join('\n') ?? '',
  partialLookbackWindow: String(config?.partialLookbackWindow ?? ''),
  validationRegex: config?.validationRegex ?? '',
});

type Props = {
  data: unknown;
  onUnsavedChange?: (dirty: boolean) => void;
};

export const PassportConfigForm: FC<Props> = ({ data, onUnsavedChange }) => {
  const { t } = useTranslation('anonymizerConfig');
  const canEdit = useHasPermission('ADMIN_ANONYMIZE_CONFIG');
  const config = data as PassportConfig | undefined;
  const { mutate: updateConfig, isPending } = useUpdateAnonymizerConfig();
  const { refetch } = useAnonymizerConfig('passport');

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(config),
  });

  useEffect(() => {
    if (config) {
      reset(getDefaultValues(config));
    }
  }, [config, reset]);

  useEffect(() => {
    onUnsavedChange?.(isDirty);
  }, [isDirty, onUnsavedChange]);

  const onSubmit = handleSubmit((values) => {
    if (!config) return;
    const body: PassportConfig = {
      fullPassportRegex: values.fullPassportRegex || config.fullPassportRegex,
      contextMarkers: values.contextMarkers ? values.contextMarkers.split('\n').filter(Boolean) : config.contextMarkers,
      lookbackWindow: values.lookbackWindow ? Number(values.lookbackWindow) : config.lookbackWindow,
      partialPassportRegex: values.partialPassportRegex || config.partialPassportRegex,
      partialContextMarkers: values.partialContextMarkers
        ? values.partialContextMarkers.split('\n').filter(Boolean)
        : config.partialContextMarkers,
      partialLookbackWindow: values.partialLookbackWindow
        ? Number(values.partialLookbackWindow)
        : config.partialLookbackWindow,
      validationRegex: values.validationRegex || config.validationRegex,
    };
    updateConfig({ className: 'passport', body });
  });

  const handleReset = () => {
    reset(getDefaultValues(config));
    refetch();
  };

  return (
    <ConfigFormLayout
      title={t('classes.passport')}
      isDirty={isDirty}
      onSave={onSubmit}
      onReset={handleReset}
      isSaving={isPending}
    >
      <div className="flex flex-col gap-6 [&_label]:text-foreground">
        <Textarea
          control={control}
          name="fullPassportRegex"
          label={t('fields.fullPassportRegex')}
          disabled={!canEdit}
          rows={3}
        />
        <Textarea
          control={control}
          name="contextMarkers"
          label={t('fields.fullContextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="lookbackWindow"
          label={t('fields.lookbackWindow')}
          disabled={!canEdit}
          rows={1}
        />
        <Textarea
          control={control}
          name="partialPassportRegex"
          label={t('fields.partialPassportRegex')}
          disabled={!canEdit}
          rows={3}
        />
        <Textarea
          control={control}
          name="partialContextMarkers"
          label={t('fields.partialContextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="partialLookbackWindow"
          label={t('fields.partialLookbackWindow')}
          disabled={!canEdit}
          rows={1}
        />
        <Textarea
          control={control}
          name="validationRegex"
          label={t('fields.validationRegex')}
          disabled={!canEdit}
          rows={3}
        />
      </div>
    </ConfigFormLayout>
  );
};
